import type { Batch, HistoryEntry, Sample, TrayLoad, TrayUnload } from '@ctcells/contract'
import { hasMessage, t } from '@ctcells/i18n'

import {
  assignmentSampleGroups,
  sampleWellsOf,
  serverSampleGroups,
} from '../monitoring/serverSampleGroups.ts'
import type { SampleGroupShape } from '../recipes/recipeForm.ts'
import { trayWellRank } from '../sortRanks.ts'

/**
 * 작업 상세가 쓰는 **고르기 함수들**. 화면도 컨테이너도 이것만 부른다.
 *
 * 🔴 **여기 있는 것은 전부 「응답에 있는 값으로 거르기」다.** 상태를 유도하거나 값을 지어내는
 *    코드를 넣지 마라 — 그 순간 프론트가 상태를 추론하는 것이 된다.
 * 🔴 **상시 작업 줄(`BatchStrip`)이 나중에 같은 것을 쓴다**(「확약 대기 N」 배지). 그래서
 *    화면 파일이 아니라 여기 산다 — 두 벌로 갈라지면 같은 수를 다르게 센다.
 */

/**
 * 이력 한 줄이 이 배치의 것인가.
 *
 * 🔴 **접두어가 붙어 온다.** 서버가 `"batch:"` 를 직접 이어 붙인다
 *    (`app/services/history.py` `_event_select` · `audit.compose`).
 * ⚠ **`command` 원장은 이 판정을 통과할 수 없다** — 그 원장의 `target` 은 **접두어 없는 원본**
 *   (모듈 id)이고 `batch_id` 열은 `/history` 응답에 실리지 않는다. 그래서 「명령」 탭은
 *   `event_type=audit` 으로 본다(2026-09-20 결정 · `docs/backend-requests.csv` BR-17).
 */
export function isBatchTarget(entry: HistoryEntry, batchId: string): boolean {
  return entry.target === `batch:${batchId}`
}

/** 이력 목록에서 이 배치 것만. 서버가 `ts` 내림차순으로 주고 **순서를 바꾸지 않는다** */
export function historyForBatch(
  entries: readonly HistoryEntry[] | undefined,
  batchId: string,
): readonly HistoryEntry[] {
  return (entries ?? []).filter((entry) => isBatchTarget(entry, batchId))
}

/** 적재·수거 원장에서 이 배치 것만. 두 DTO 다 `batch_id` 가 **필수**다 */
export function traysForBatch<T extends { batch_id: string }>(
  rows: readonly T[] | undefined,
  batchId: string,
): readonly T[] {
  return (rows ?? []).filter((row) => row.batch_id === batchId)
}

/**
 * 확약 대기 건 — 🔴 **판정 축은 `confirmed_at === null` 하나다.**
 *    `Batch.allowed_commands` 와 섞지 마라. 그 enum 다섯에 확약이 **없다**.
 * ⚠ `== null` 로 본다 — 서버는 키를 주고 값만 `null` 이다.
 */
export function pendingTrays<T extends { confirmed_at?: string | null }>(
  rows: readonly T[],
): readonly T[] {
  return rows.filter((row) => row.confirmed_at == null)
}

/** 적재·수거를 합친 「확약 대기」 수. 상시 작업 줄 배지가 쓸 자리다 */
export function pendingConfirmCount(
  loads: readonly TrayLoad[],
  unloads: readonly TrayUnload[],
): number {
  return pendingTrays(loads).length + pendingTrays(unloads).length
}

/** `HistoryEntry.detail` 은 자유 객체다. 문자열 값만 안전하게 꺼낸다 */
export function detailText(entry: HistoryEntry, key: string): string | undefined {
  const value = entry.detail?.[key]
  return typeof value === 'string' ? value : undefined
}

/**
 * 감사 행의 **동작**(`detail.operation`) 표기.
 *
 * 🔴 **원값을 그냥 흘리지 않는다**(§1-1 — enum 영문 노출 금지). 사전을 먼저 본다.
 * ⚠ 그렇다고 `enumLabel()` 을 쓰지 않는다 — 그쪽은 모르는 값을 `—` 로 **떨어뜨린다.**
 *   여기 오는 값은 서버가 자유롭게 늘리는 연산 이름이라, 모르는 것을 `—` 로 지우면
 *   **무슨 명령이었는지가 통째로 사라진다.** 사전에 없으면 원문이 정보다.
 */
export function operationText(value: string | undefined): string {
  if (value == null || value === '') return t('mask.none')
  const key = `enum.operation.${value}`
  return hasMessage(key) ? t(key) : value
}

/**
 * 감사 행의 `detail.reason` 이 **서버가 합성한 채움말**인가.
 *
 * 🔴 **`reason` 은 두 가지가 섞여 온다**(2026-09-20 실물 확인). 백엔드가
 *    `reason or synthesize_reason(...)` 로 적는다(`app/views/batches.py` `_write_audit`) —
 *      ⓐ **사람이 적은 사유**: 중단·복구처럼 계약이 `reason` 을 요구하는 명령
 *      ⓑ **서버 합성**: 그 밖(`createBatch`·`startBatch`·`pauseBatch` …). `IFC-COM-15 ⓑ`
 *    ⓑ 는 이런 꼴이다 — `app/services/users.py` `synthesize_reason` 이 조립한다:
 *    ```
 *    operation=startBatch target=e1f7fa76-… actor=dev-admin command_id=f13353b1-… changed=none
 *    ```
 * 🔴 **그대로 화면에 뿌리면 사유 열이 기계 문자열로 터진다**(실물에서 그랬다 — UUID 두 개가
 *    가로로 뻗어 뒤 열들을 밀어냈다). 게다가 **그 안의 값은 전부 다른 열에 이미 있다**:
 *    `operation` = 「동작」 · `actor` = 「행위자」 · `target` = 이 화면이 보고 있는 배치 ·
 *    `command_id` = 「명령 ID」 열. **사유 열에 남을 새 정보가 0 이다.**
 *
 * ⚠ **판정은 «문자열 전체»가 이 꼴일 때만** 한다. 사람이 적은 사유가 우연히 `operation=` 로
 *   시작한다고 지우면 **진짜 사유를 잃는다** — 그쪽이 훨씬 나쁜 오류다.
 * ⚠ `changed=` 조각은 `users.py` 판에만 있다(`audit.py` 판은 없다). 둘 다 받는다.
 */
const SYNTHESIZED_REASON =
  /^operation=(\S+) target=(\S+) actor=(\S+) command_id=(\S+)(?: changed=(\S+))?$/

export interface SynthesizedReason {
  /** `command_id=none` 이면 `undefined` — 서버가 「없다」를 그 낱말로 적는다 */
  commandId: string | undefined
}

export function synthesizedReason(text: string | undefined): SynthesizedReason | undefined {
  if (text == null) return undefined
  const hit = SYNTHESIZED_REASON.exec(text)
  if (hit === null) return undefined
  const commandId = hit[4]
  return { commandId: commandId === undefined || commandId === 'none' ? undefined : commandId }
}

/**
 * 「사유」 열에 그릴 값 — **사람이 적은 것만**.
 * 🔴 합성분은 `undefined` 다. 사유가 «없었다»는 것이 사실이고, 화면은 `—` 로 그린다.
 */
export function humanReason(entry: HistoryEntry): string | undefined {
  const text = detailText(entry, 'reason')
  return synthesizedReason(text) === undefined ? text : undefined
}

/**
 * 「명령 ID」 열에 그릴 값.
 *
 * 🔴 **감사 원장의 `detail` 에 `command_id` 키가 없다**(실물 — `{action, reason, operation,
 *    from_status}` 뿐). 그런데 **그 값은 합성 사유 문자열 안에 들어 있다.** 서버가 이미 준
 *    값을 화면이 못 쓰고 `—` 를 찍고 있었다 — 되찾는다.
 * ⚠ **지어내는 것이 아니다.** 같은 응답 안의 값을 옮겨 읽을 뿐이고, 없으면 `undefined` 다.
 *   서버가 `detail.command_id` 를 싣기 시작하면 그쪽이 먼저다.
 */
export function commandIdOf(entry: HistoryEntry): string | undefined {
  return (
    detailText(entry, 'command_id') ?? synthesizedReason(detailText(entry, 'reason'))?.commandId
  )
}

/**
 * 작업 상세 「시료」 표의 한 줄 — **행 하나가 시료 하나다**(eric 2026-09-30 「시료 번호가 맨 앞에 오고... 웰이.. 두개씩」).
 *
 * - `sampleId` — 시료 번호
 * - `wells` — 그 시료의 챔버(웰) **전부**. 레시피 묶음의 짝(`A1 · A2`)이 먼저고(`sampleWellsOf` — 모니터링과 같은 함수),
 *   묶음을 모르면(레시피를 못 받음 · 묶음 밖) 서버 배정 웰 하나다. 🔴 **빈 배열은 「배정 없음」**이다
 * - `sample` — `GET /samples` 에서 `sample_id` 로 찾은 행. 못 찾았거나 못 봤으면 `undefined`
 */
export interface SampleRow {
  sampleId: string
  wells: readonly string[]
  sample: Sample | undefined
  /**
   * 그 시료가 앉은 **칸 이름**(`P1` · `REF` — 작업 추가 창 칸표의 「칸」 · eric 2026-10-22 「작업 추가의 내용이 보이도록」).
   * 시료 응답을 봤으면 서버 `sample_ref`, 못 봤으면(viewer) 레시피 묶음에서 그 시료의 배정 웰이 든 묶음의 표지다.
   * 🔴 둘 다 모르면 `undefined` — 지어내지 않는다
   */
  sampleRef: string | undefined
}

/**
 * 🔴 **같은 시료를 두 번 그리지 않는다** — 배정(`sample_assignments`)과 시료 응답을 한 줄로 잇는다.
 *
 * - **짝 웰은 모니터링과 같은 규칙이다.** 시료 응답을 봤으면 `serverSampleGroups`(`sample_ref` · `assigned_well`),
 *   못 봤으면(viewer 403 · `samples === undefined`) `assignmentSampleGroups`(배정 웰) — `useSampleFlow` 의 그 갈래다.
 *   ⚠ 모니터링은 서버가 배정을 안 준 옛 회차에서 **임시 목**(`temporarySampleGroups`)으로 떨어지지만 여기는 떨어지지
 *   않는다 — 작업 상세는 서버 배정만 적는다(목의 짝을 사실처럼 적지 않는다)
 * - 차례: **첫 웰의 트레이 차례(`slots`)** → 트레이 밖 웰(서버 차례) → **배정 없는 시료**(시료 응답 차례)
 *   ⚠ 트레이 칸 수는 응답 어디에도 없다 — 호출부가 `TRAY_SLOTS`(단일 출처)를 넘긴다
 * ⚠ 배정 밖 시료가 생기는 까닭 — `sample_count`(지금 붙은 시료)와 `sample_assignments`(자리가 정해진 시료)는
 *   다른 축이다(`screen.ts` `sample_count` 머리말). 둘을 억지로 맞추지 않는다.
 * ⚠ 배정 웰은 «배정» 축이다 — 현재 위치(`Sample.current_position`)와 달라도 결함이 아니다.
 */
export function sampleRowsOf({
  assignments,
  historicalIds,
  samples,
  groups,
  slots,
}: {
  assignments: Batch['sample_assignments']
  historicalIds?: readonly string[] | undefined
  /** `undefined` = 시료 응답을 못 봤다(viewer). 빈 배열(0건)과 다르다 */
  samples: readonly Sample[] | undefined
  groups: readonly SampleGroupShape[]
  slots: readonly string[]
}): SampleRow[] {
  const byId = new Map((samples ?? []).map((sample) => [sample.sample_id, sample]))
  const assign =
    samples === undefined
      ? assignmentSampleGroups(assignments)
      : (serverSampleGroups(samples) ?? assignmentSampleGroups(assignments))
  const paired = sampleWellsOf(groups, assign)

  const ids: string[] = []
  const seen = new Set<string>()
  const add = (id: string) => {
    if (!seen.has(id)) {
      seen.add(id)
      ids.push(id)
    }
  }
  if (historicalIds !== undefined) historicalIds.forEach(add)
  else {
    assignments.forEach((a) => add(a.sample_id))
    ;(samples ?? []).forEach((sample) => add(sample.sample_id))
  }

  /*
   * 🔴 **다른 시료에 배정된 웰은 짝에서 뺀다**(eric 2026-10-08 캡처 — `-01` 은 「A1」인데 `-02` 는 「A1, B1」). v0.9.3 은 시료 1건 = 챔버 1개라
   *    한 환자 묶음(A1 · B1)에 시료가 둘 든다. 묶음 대표 시료에 묶음 웰을 통째로 붙이면 남의 웰(A1)까지 제 것처럼 적힌다. 짝은 «아무도 배정받지
   *    않은 짝 웰»만 채운다 — 시료 하나가 묶음 전체를 맡던 옛 회차(배정 웰 하나 + 짝)는 그대로 「A1, B1」이다
   */
  const owner = new Map(assignments.map((a) => [a.wellName, a.sample_id]))
  const rows = ids.map((sampleId): SampleRow => {
    const told = assignments.filter((a) => a.sample_id === sampleId).map((a) => a.wellName)
    const pairedWells = (paired.get(sampleId) ?? told).filter((well) => {
      const who = owner.get(well)
      return who === undefined || who === sampleId
    })
    const sample = byId.get(sampleId)
    const firstWell = told[0] ?? (historicalIds === undefined ? sample?.assigned_well : undefined)
    const group =
      firstWell === undefined ? undefined : groups.find((g) => g.wells.includes(firstWell))
    const ref = sample?.sample_ref
    const fromServer = ref == null || ref === '' ? undefined : ref
    return {
      sampleId,
      wells: historicalIds !== undefined ? told : pairedWells.length > 0 ? pairedWells : told,
      sample,
      sampleRef: historicalIds !== undefined ? group?.sampleRef : (fromServer ?? group?.sampleRef),
    }
  })

  /*
   * 첫 웰의 트레이 차례 · 트레이 밖은 그 뒤 · 웰 없음은 맨 뒤. 같은 자리끼리는 들어온 차례(안정 정렬).
   * 🔴 「웰」 머리글 정렬과 **같은 등급 함수**다(`sortRanks.ts` `trayWellRank`) — 처음 차례와 머리글 오름차순이 갈리지 않게
   */
  const rank = (row: SampleRow): number => trayWellRank(row.wells, slots) ?? slots.length + 1
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index)
    .map(({ row }) => row)
}

/**
 * 작업 상세(서랍) **안의 표**가 폭이 모자랄 때 열이 빠지는 차례 — 낮을수록 먼저 빠진다. **여기 없는 열은 늘 보인다.**
 * (eric 2026-09-30 「1/3 정도만 drawer 형태로」 — 서랍이 화면의 약 1/3 로 좁아져 카드 안 표가 넘쳤다.)
 *
 * 🔴 빠진 열의 값은 **그 행을 펼치면** 선다(`DataTable` `revealHidden`) — 서랍 안의 표는 값이 갈 다른 자리가 없다.
 * 🔴 첫 열(시각 · 자리)은 줘도 안 빠진다. 판단에 쓰는 열(확약)과 단추 열(확약)은 넣지 않는다.
 *
 * - 레시피: 생성(2026-10-22 · 1280 서랍에서 [상세] 단추가 카드 밖으로 밀렸다). **이름 · 버전 · [상세]는 남는다**
 * - 시료: 타입 → 단계(eric 2026-10-22 — 작업 추가 창 칸표의 열로 늘었다). **칸 · 웰 · 시료 번호 · 상태는 남는다**.
 *   좁으면 먼저 시료 번호를 끝자리로 줄이고(`SAMPLE_ID_COMPACT` · priority 0) 그다음 빠진다
 * - 명령: 사유 → 작업자(admin 에게만 있는 열). **시각 · 동작은 남는다**. 명령 ID 열은 없다(eric 2026-09-30)
 *   🔴 **사유가 먼저 빠진다**(팀장 결정 2026-09-30) — 「누가 · 언제 · 무엇을」이 한 줄에 서는 것이 이 표의 일이고,
 *   긴 사유(중단 · 폐기)는 한 칸에서 잘려 어차피 못 읽는다 → 행을 펼쳐 온전히 읽는다. 종전 차례(작업자 먼저)는
 *   admin 에게도 좁은 서랍에서 작업자를 기본으로 감췄다. 네 열이 다 들어가면 넷 다 선다
 * - 적재 · 수거: 출처 → 행위자 → 적재 챔버 → 열림. **자리 · 확약 · 확약 단추는 남는다**
 */
export const DETAIL_TABLE_PRIORITY = {
  recipe: { created: 1 },
  samples: { type: 1, stage: 2 },
  commands: { reason: 1, actor: 2 },
  trays: { source: 1, by: 2, chambers: 3, opened: 4 },
} as const satisfies Record<string, Record<string, number>>

/**
 * 작업 상세 시료 표의 **시료 번호 줄인 꼴** — 표가 카드를 넘칠 때만 일어난다(쵸단 실측 2026-09-30 ·
 * 1440 서랍 1/3 에서 `TEST-20260930-095822-01` 때문에 표가 카드를 넘어 오른쪽 열이 잘렸다).
 *
 * - `priority` 는 열 맞춤의 차례(`columnFit.ts`). 시료 표에는 빠질 열이 없어 이 한 단계뿐이다
 * - `tail` 은 남기는 끝 글자 수 — 시료 번호는 **끝의 일련번호가 서로 가른다**(`…095822-01` · `…260930-01`).
 *   앞 8자 꼴(`ShortId` 기본)은 한 회차의 시료가 전부 `TEST-202…` 로 같아 보인다
 * 🔴 전체 값은 `title` · 보조기술 글자(`ShortId`)와 행 펼침(`revealHidden`)에 남는다
 */
export const SAMPLE_ID_COMPACT = { priority: 0, tail: 9 } as const

/** 열 정의 묶음에 위 표의 차례를 얹는다. 표에 없는 열은 그대로(늘 보인다) */
export function withDetailPriority<C extends { key: string }>(
  table: keyof typeof DETAIL_TABLE_PRIORITY,
  columns: readonly C[],
): (C & { priority: number | undefined })[] {
  const order: Record<string, number> = DETAIL_TABLE_PRIORITY[table]
  return columns.map((column) => ({ ...column, priority: order[column.key] }))
}
